/**
 * /deposit-release/:token — where the lessor's representative releases a deposit.
 *
 * Public and unauthenticated on purpose. A lessor is a record with an email, not
 * necessarily a user account, so requiring a login would exclude most of them.
 * The token is the credential: 32 random bytes, stored only as a SHA-256 hash,
 * validated in the edge function.
 *
 * German throughout — the recipient is a German lessor, and every other outbound
 * document in this system is German.
 *
 * Every failure says the same thing. Distinguishing "no such link" from "expired"
 * would let someone probe which links ever existed, and the remedy is identical
 * either way: ask Coliviq for a fresh one.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchReleaseState,
  type ReleaseAuth,
  type ReleaseReviewState,
} from "@/lib/depositReleaseApi";
import { DepositReleasePanel } from "@/components/deposit/DepositReleasePanel";

const INVALID_MESSAGE =
  "Dieser Link ist nicht mehr gültig. Bitte wenden Sie sich an coliviq, um einen neuen Link zu erhalten.";

export default function DepositRelease() {
  const { token = "" } = useParams<{ token: string }>();
  const [state, setState] = useState<ReleaseReviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const auth: ReleaseAuth = { mode: "token", token };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReleaseState({ mode: "token", token })
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch(() => {
        // Deliberately not the server's message: every rejection reads the same.
        if (!cancelled) setError(INVALID_MESSAGE);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F4F6F7",
        padding: "40px 20px 80px",
        fontFamily:
          '-apple-system, "SF Pro Text", Inter, system-ui, sans-serif',
        color: "#0F172A",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.13em",
            textTransform: "uppercase",
            color: "#0F5C52",
            marginBottom: 18,
          }}
        >
          coliviq
        </div>

        {loading && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #ECEEF1",
              borderRadius: 20,
              padding: "40px 24px",
              textAlign: "center",
              fontSize: 14,
              color: "#64748B",
            }}
          >
            Unterlagen werden geladen…
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #ECEEF1",
              borderRadius: 20,
              padding: "32px 26px",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              Link nicht gültig
            </div>
            <div
              style={{
                fontSize: 13.5,
                color: "#64748B",
                marginTop: 8,
                lineHeight: 1.6,
              }}
            >
              {error}
            </div>
            <a
              href="mailto:kontakt@coliviq.de"
              style={{
                display: "inline-block",
                marginTop: 18,
                fontSize: 13.5,
                fontWeight: 700,
                color: "#6D28D9",
                textDecoration: "none",
              }}
            >
              kontakt@coliviq.de
            </a>
          </div>
        )}

        {!loading && !error && state && (
          <DepositReleasePanel
            auth={auth}
            state={state}
            onState={setState}
            locale="de"
          />
        )}

        <div
          style={{
            fontSize: 11.5,
            color: "#94A3B8",
            textAlign: "center",
            marginTop: 24,
            lineHeight: 1.6,
          }}
        >
          coliviq · kontakt@coliviq.de
          <br />
          Bitte leiten Sie diesen Link nicht weiter.
        </div>
      </div>
    </div>
  );
}
